import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

/**
 * The product's one button.
 *
 * Variants exist for the *role* an action plays, never for a colour someone
 * wanted: `primary` is the single most important action on a surface, `secondary`
 * is everything else with a border, `ghost` is toolbar-weight, `danger` is
 * destructive, `link` is inline navigation. If a screen seems to need two
 * primaries, one of them is not primary.
 */
const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap',
    'rounded-md font-medium transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-fg shadow-xs hover:bg-primary-hover',
        secondary:
          'border border-border bg-surface text-fg shadow-xs hover:bg-surface-hover hover:border-border-strong',
        ghost: 'text-fg-secondary hover:bg-surface-hover hover:text-fg',
        subtle: 'bg-surface-hover text-fg hover:bg-surface-active',
        accent: 'bg-accent text-accent-fg shadow-xs hover:bg-accent-hover',
        danger: 'bg-danger text-white shadow-xs hover:bg-danger-hover',
        'danger-ghost': 'text-danger hover:bg-danger-bg',
        link: 'text-accent-text underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-control-sm px-2.5 text-xs [&_svg]:size-3.5',
        md: 'h-control px-3 text-[0.8125rem] [&_svg]:size-4',
        lg: 'h-control-lg px-4 text-sm [&_svg]:size-4',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'secondary', size: 'md', block: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a <button> (e.g. a router <Link>). */
  asChild?: boolean;
  /** Show a spinner and block interaction while an action is in flight. */
  loading?: boolean;
  icon?: ReactNode;
  iconEnd?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, asChild, loading, icon, iconEnd, children, disabled, ...props },
  ref,
) {
  if (asChild) {
    /*
     * `asChild` hands the styling to the caller's own element (a router Link, an
     * anchor). Radix's Slot needs EXACTLY ONE element child, and the icon slots
     * below would make three — including `undefined` ones, which still count.
     * So the caller composes its own content; the icons are theirs to place.
     */
    return (
      <Slot.Root
        ref={ref}
        className={cn(buttonVariants({ variant, size, block }), className)}
        aria-busy={loading || undefined}
        {...props}
      >
        {children}
      </Slot.Root>
    );
  }

  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, block }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : icon}
      {children}
      {iconEnd}
    </button>
  );
});

const iconButtonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center rounded-md transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        ghost: 'text-fg-muted hover:bg-surface-hover hover:text-fg',
        secondary: 'border border-border bg-surface text-fg-secondary shadow-xs hover:bg-surface-hover',
        primary: 'bg-primary text-primary-fg shadow-xs hover:bg-primary-hover',
        accent: 'bg-accent text-accent-fg shadow-xs hover:bg-accent-hover',
        danger: 'text-danger hover:bg-danger-bg',
      },
      size: {
        sm: 'size-7 [&_svg]:size-3.5',
        md: 'size-8 [&_svg]:size-4',
        lg: 'size-9 [&_svg]:size-[1.125rem]',
      },
      active: { true: '', false: '' },
    },
    compoundVariants: [
      { variant: 'ghost', active: true, className: 'bg-surface-active text-fg' },
      { variant: 'secondary', active: true, className: 'border-border-strong bg-surface-active text-fg' },
    ],
    defaultVariants: { variant: 'ghost', size: 'md', active: false },
  },
);

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  /** Required: an icon-only control has no accessible name without it. */
  label: string;
  asChild?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, variant, size, active, label, asChild, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      ref={ref}
      aria-label={label}
      title={label}
      className={cn(iconButtonVariants({ variant, size, active }), className)}
      {...props}
    >
      {children}
    </Comp>
  );
});

export { buttonVariants, iconButtonVariants };
